/**
 * SafetyScore — visual indicator for messaging rate safety.
 * Green = safe, Yellow = moderate, Red = risky.
 */

import { Badge } from '@/components/ui/badge'
import { Shield, ShieldAlert, ShieldCheck } from 'lucide-react'

type SafetyScoreProps = {
  readonly messagesPerHour: number
  readonly maxDailyMessages: number
}

type SafetyLevel = 'safe' | 'moderate' | 'risky'

function computeLevel(mph: number, mdm: number): SafetyLevel {
  if (mph <= 5 && mdm <= 30) return 'safe'
  if (mph <= 10 && mdm <= 60) return 'moderate'
  return 'risky'
}

const LEVEL_CONFIG: Record<SafetyLevel, {
  label: string
  description: string
  color: string
  Icon: typeof Shield
}> = {
  safe: {
    label: 'Safe',
    description: 'Conservative settings — low risk of LinkedIn restrictions.',
    color: 'bg-green-100 text-green-800 border-green-200',
    Icon: ShieldCheck,
  },
  moderate: {
    label: 'Moderate',
    description: 'Moderate activity — monitor your account for warnings.',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Icon: Shield,
  },
  risky: {
    label: 'Risky',
    description: 'High activity — increased risk of LinkedIn restrictions.',
    color: 'bg-red-100 text-red-800 border-red-200',
    Icon: ShieldAlert,
  },
}

export function SafetyScore({ messagesPerHour, maxDailyMessages }: SafetyScoreProps) {
  const level = computeLevel(messagesPerHour, maxDailyMessages)
  const { label, description, color, Icon } = LEVEL_CONFIG[level]

  return (
    <div className="flex items-start gap-3 rounded-lg border p-3">
      <Badge variant="outline" className={`gap-1.5 shrink-0 ${color}`}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </Badge>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  )
}
